import type { Dayjs } from "dayjs";
import type { VenuePageTimezones } from "@punchup/data/models/venue_pages";
import dayjs from "@punchup/utils/dayjs";

/**
 * Venue-local day arithmetic for `comedian_shows.datetime`.
 *
 * `datetime` is `timestamp without time zone`: venue-local wall-clock digits
 * carrying no offset. Two rules follow, and every helper here obeys them.
 *
 * 1. **A query bound is rendered with `.format(...)`, never `.toISOString()`.**
 *    UTC rendering skews the boundary by the venue's offset — 4–5 hours for a
 *    US venue, enough to drop tonight's show from "today" at 8pm ET.
 * 2. **A show's calendar day is read off its own digits, not off an instant.**
 *    Parsing the digits produces an instant in whatever zone the runtime
 *    happens to be in; converting that instant into the venue's zone shifts a
 *    late-night show onto the wrong date. The digits already ARE the local day.
 *
 * Shared by the public "shows tonight" page and the venue dashboard's Box
 * Office list so a show cannot be "today" on one surface and not the other.
 */

/** Naive wall-clock render, for comparing against the tz-naive `datetime` column. */
const WALL_CLOCK_FORMAT = "YYYY-MM-DDTHH:mm:ss";

/** Calendar-day key. Lexicographic order matches chronological order. */
const DAY_KEY_FORMAT = "YYYY-MM-DD";

/**
 * Start of *today* in each of a venue page's local timezones.
 *
 * A multi-location page spanning zones (ET first + PT second) must not hide the
 * PT location's 10pm show at 9pm PT just because the ET clock hit midnight, so
 * callers take the earliest of these; the cost is that eastern locations' shows
 * linger a few extra hours, which is the safe direction.
 */
export function localStartsOfToday(
  venueTimezones: VenuePageTimezones
): Dayjs[] {
  return venueTimezones.map((entry) =>
    dayjs().tz(entry.timezone).startOf("day")
  );
}

export interface VenueDayWindow {
  /** Inclusive lower bound, naive wall-clock. */
  start: string;
  /** Inclusive upper bound, naive wall-clock. */
  end: string;
}

/**
 * The query bounds covering `days` venue-local calendar days starting
 * `startOffset` days from today — earliest local start-of-the-first-day through
 * latest local end-of-the-last-day. `startOffset` 0 starts today.
 *
 * The window is deliberately wider than the answer when a page spans zones: it
 * is only the database filter, and {@link showLocalDayOffset} narrows each row
 * against its own location's zone afterwards.
 *
 * With no timezone data (`venue_pages.timezones` is legitimately empty when the
 * recompute found no coordinates) this falls back to the runtime's own day. In
 * a browser that is the operator's machine, which at a venue IS the venue —
 * a better guess than UTC, which would hide a US venue's evening shows.
 */
export function venueDayWindow(
  venueTimezones: VenuePageTimezones,
  days = 1,
  startOffset = 0
): VenueDayWindow {
  const lastDayOffset = startOffset + Math.max(0, days - 1);

  if (!venueTimezones?.length) {
    const today = dayjs().startOf("day");
    return {
      start: today.add(startOffset, "day").format(WALL_CLOCK_FORMAT),
      end: today
        .add(lastDayOffset, "day")
        .endOf("day")
        .format(WALL_CLOCK_FORMAT),
    };
  }

  const starts = localStartsOfToday(venueTimezones);
  // Identical fixed-width formats ⇒ lexicographic min/max = chronological
  // min/max of the wall-clock digits, the right axis for a tz-naive column.
  const startKeys = starts
    .map((d) => d.add(startOffset, "day").format(WALL_CLOCK_FORMAT))
    .sort();
  const endKeys = starts
    .map((d) =>
      d.add(lastDayOffset, "day").endOf("day").format(WALL_CLOCK_FORMAT)
    )
    .sort();

  return { start: startKeys[0], end: endKeys[endKeys.length - 1] };
}

/**
 * The timezone a show's own location sits in. Falls back to the runtime's zone
 * for a show whose `venue_id` was never backfilled (missing coordinates).
 */
function resolveShowTimezone(
  venueTimezones: VenuePageTimezones,
  venueId: string | null | undefined
): string {
  const entry = venueId
    ? venueTimezones.find((t) => t.location_id === venueId)
    : undefined;
  return entry?.timezone ?? venueTimezones[0]?.timezone ?? dayjs.tz.guess();
}

/**
 * The calendar day a `comedian_shows.datetime` names, as `YYYY-MM-DD`.
 *
 * Read straight off the digits — see rule 2 above. The slice handles the
 * column's own shape; the `dayjs` path is the fallback for any other string a
 * caller hands in.
 */
function showLocalDayKey(datetime: string | null): string | null {
  if (!datetime) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(datetime)) return datetime.slice(0, 10);
  const parsed = dayjs(datetime);
  return parsed.isValid() ? parsed.format(DAY_KEY_FORMAT) : null;
}

export interface ShowWithLocalDay {
  datetime: string | null;
  venue_id?: string | null;
}

/**
 * How many venue-local days away a show is: `0` today, `1` tomorrow, negative
 * for past, `null` when the show has no date.
 *
 * "Today" is resolved against the show's OWN location's timezone, so a
 * producer or tour page bucketing across cities filters each row correctly.
 */
export function showLocalDayOffset(
  show: ShowWithLocalDay,
  venueTimezones: VenuePageTimezones
): number | null {
  const showKey = showLocalDayKey(show.datetime);
  if (!showKey) return null;

  const timezone = resolveShowTimezone(venueTimezones, show.venue_id);
  const todayKey = dayjs().tz(timezone).format(DAY_KEY_FORMAT);

  // Both keys are parsed as UTC, NOT as local dates. A bare `dayjs("2026-03-08")`
  // is midnight in whatever zone the runtime is in — and in a zone that springs
  // forward AT midnight (Havana, Santiago, Asunción, Tehran) that midnight does
  // not exist, so it lands on 01:00 and the day difference comes back 0 instead
  // of 1: tomorrow's show would file under Today. UTC has no transitions, so
  // this subtraction is exact everywhere.
  return dayjs.utc(showKey).diff(dayjs.utc(todayKey), "day");
}
